/**
 * Personal activity prediction through a dedicated WASM/WebGPU worker. User/token checks bracket
 * model work and bounded writes; manual fields are preserved and final scheduling remains a server
 * responsibility.
 *
 * Requests run through the page-wide compute scheduler as the `inference` adapter on the
 * `interactive` lane: a newer request supersedes an older one instead of being rejected, a
 * 20-second deadline bounds queue plus run time, and background work pauses while it runs. A
 * pool keeps one never-used Worker with the WASM module already loaded, so the first request of
 * a session skips module fetch and compilation; see docs/developer/browser-compute.md.
 *
 * @module apps/web/src/lib/browserInference
 */

import type { TaskResponse, TaskUI } from '@fluxability/types';
import { api, request } from './api';
import {
  activityTarget,
  personalObservationsChunked,
  predictionPatch,
  type InferenceBatch,
} from './browserInferenceModel';
import { useUserStore } from '@/stores/userStore';
import { computeScheduler, createPoolPolicy, WarmWorkerPool } from './compute';
import { InferenceWorkerClient, warmInferenceWorker } from './inferenceWorkerClient';

/**
 * Export at most 512 raw history observations, exclude team evidence, predict missing activity
 * fields, and apply eligible patches with at most four API writes in flight. Rechecks the captured
 * user and bearer token before work and each write batch. Empty evidence is a successful no-op;
 * progress callbacks report completed updates.
 */
export async function inferActivityPlanning(
  tasks: TaskUI[],
  onProgress?: (message: string) => void
) {
  const userId = useUserStore.getState().userId;
  const token = localStorage.getItem('flexiplanner-session-token');
  const ensureSession = () => {
    if (
      !userId ||
      useUserStore.getState().userId !== userId ||
      localStorage.getItem('flexiplanner-session-token') !== token
    )
      throw new Error('Your session changed. Run inference again after signing in.');
  };
  ensureSession();
  // Team-owned evidence is not exported into the personal model.
  const evidence = await request<unknown>('/training/export', {
    method: 'POST',
    body: JSON.stringify({ format: 'json', includeRaw: true, offset: 0, limit: 512 }),
  });
  ensureSession();
  if (!evidence.success)
    throw new Error(
      evidence.error?.message || 'Personal history is unavailable. Fields were left unchanged.'
    );
  // Chunked so conversion of up to 512 rows never joins React work in one long task.
  const observations = await personalObservationsChunked(evidence.data);
  ensureSession();
  if (!observations.length)
    return {
      success: true,
      data: { applied: 0, skipped: tasks.length, tasks: [], backend: 'no_evidence' },
    };
  const batches: InferenceBatch[] = await workerInference(tasks.map(activityTarget), observations);
  ensureSession();
  const predictions = new Map(
    batches.flatMap(batch =>
      batch.predictions.map(
        prediction => [prediction.activity_id, { prediction, telemetry: batch.telemetry }] as const
      )
    )
  );
  const applicable = tasks.flatMap(task => {
    const match = predictions.get(task.id);
    if (!match) return [];
    const patch = predictionPatch(task, match.prediction, match.telemetry);
    return patch ? [{ task, patch }] : [];
  });
  const saved: (TaskResponse | undefined)[] = [];
  onProgress?.(`Applying ${applicable.length} supported personal predictions…`);
  // Four bounded requests at a time. Manual entries are preserved by predictionPatch.
  for (let offset = 0; offset < applicable.length; offset += 4) {
    ensureSession();
    const results = await Promise.all(
      applicable.slice(offset, offset + 4).map(async ({ task, patch }) => {
        ensureSession();
        const response = await api.tasks.update(task.id, patch);
        if (!response.success)
          throw new Error(response.error?.message || 'Could not save prediction.');
        return response.data;
      })
    );
    ensureSession();
    saved.push(...results);
  }
  return {
    success: true,
    data: {
      applied: saved.length,
      skipped: tasks.length - saved.length,
      tasks: saved,
      backend: batches.some(batch => batch.telemetry.backend === 'webgpu') ? 'webgpu' : 'wasm_cpu',
    },
  };
}
/** Parent-only identity binding; bearer tokens are never sent to the compute Worker. */
interface InferenceSession {
  /** Canonical signed-in person whose personal evidence may enter this Worker. */
  userId: string;
  /** Captured credential identity; a change invalidates every retained private resource. */
  token: string | null;
}

/** Create the dedicated inference module Worker (bundled by Vite). */
function createInferenceWorker(): Worker {
  return new Worker(new URL('../workers/inference.worker.ts', import.meta.url), {
    type: 'module',
    name: 'fluxability-personal-inference',
  });
}

/**
 * At most one warm, never-used Worker with the WASM module loaded. Warm Workers hold code only;
 * once the client acquires one it is session-owned and is terminated, never returned, on reset.
 */
const inferencePool = new WarmWorkerPool(createInferenceWorker, {
  warm: warmInferenceWorker,
  policy: createPoolPolicy,
});

/** One private Worker per signed-in session, taken from the warm pool on the first request. */
const inferenceClient = new InferenceWorkerClient(() => inferencePool.acquire());

/** Input of one scheduled inference request. */
interface InferenceRequest {
  /** Prediction targets (at most 2,048). */
  targets: unknown[];
  /** Converted personal observations (at most 512). */
  observations: unknown[];
}

/**
 * Deadline for one request including queue time (20 s): the client's own 15-second run timeout
 * plus headroom for a superseded predecessor to stop and for a cold module load.
 */
const INFERENCE_DEADLINE_MS = 20_000;

/** The inference Worker registered with the compute scheduler (group limit 1, interactive lane). */
const inferenceCompute = computeScheduler.register<InferenceRequest, InferenceBatch[]>({
  name: 'inference',
  lane: 'interactive',
  maxConcurrency: 1,
  deadlineMs: INFERENCE_DEADLINE_MS,
  execute: (request, signal) =>
    inferenceClient.infer(request.targets, request.observations, signal),
  prewarm: signal => inferencePool.prewarm(signal),
  reset: reason => inferenceClient.reset(reason),
});

/**
 * Ask the scheduler to warm one inference Worker at the next idle moment (background lane, so it
 * waits for input to settle). Call when inference is likely soon, such as when the Unscheduled
 * page mounts. Safe to call repeatedly: warm-ups are joined and the pool holds at most one.
 */
export function prewarmBrowserInference(): void {
  inferenceCompute.prewarm();
}

/** Identity captured when this session first requests inference. */
let inferenceSession: InferenceSession | undefined;

/** Erase personal evidence and GPU allocations before accepting work from a changed session. */
export function resetBrowserInference(): void {
  inferenceSession = undefined;
  inferenceClient.reset();
}

/** Same-tab logout, account replacement and token renewal all update the auth/user store. */
useUserStore.subscribe(state => {
  if (
    inferenceSession &&
    (state.userId !== inferenceSession.userId ||
      localStorage.getItem('flexiplanner-session-token') !== inferenceSession.token)
  )
    resetBrowserInference();
});

/** Cross-tab token replacement/clear and page navigation must not retain the previous person's data. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === null || event.key === 'flexiplanner-session-token') resetBrowserInference();
  });
  window.addEventListener('pagehide', resetBrowserInference);
}

/**
 * Reuse initialization and bounded GPU resources within one signed-in session. Recheck identity
 * before sending evidence and before returning results; all session, abort, timeout and transport
 * failure paths terminate the Worker. A concurrent call supersedes the running one through the
 * compute scheduler (the older caller rejects with reason `superseded`), so no queue builds up.
 */
export async function workerInference(
  targets: unknown[],
  observations: unknown[],
  signal?: AbortSignal
): Promise<InferenceBatch[]> {
  const userId = useUserStore.getState().userId;
  const token = localStorage.getItem('flexiplanner-session-token');
  if (!userId) {
    resetBrowserInference();
    throw new Error('Sign in before running inference.');
  }
  if (!inferenceSession || inferenceSession.userId !== userId || inferenceSession.token !== token) {
    resetBrowserInference();
    inferenceSession = { userId, token };
  }
  const owner = inferenceSession;
  // One key for every activity-planning request: a newer request supersedes the older one.
  const result = await inferenceCompute.submit(
    { targets, observations },
    { key: 'activity-planning', signal }
  );
  if (
    inferenceSession !== owner ||
    useUserStore.getState().userId !== owner.userId ||
    localStorage.getItem('flexiplanner-session-token') !== owner.token
  ) {
    resetBrowserInference();
    throw new Error('Your inference session changed.');
  }
  return result;
}
